import { cache, createAsync } from "@solidjs/router";
import {
  Accessor,
  createContext,
  createEffect,
  JSX,
  Show,
  useContext,
} from "solid-js";
import { createStore } from "solid-js/store";
import { Product } from "~/server/types/search";
import {
  getActiveBrandsFromSearchResponse,
  getStoreRouteData,
  WebStoreRouteData,
} from "~/server/data/store_route_data";
import { RnrPortalsJson } from "~/server/types/client";
import { V4HomeResponse } from "~/data/v4_homepage";
import { webEventManager } from "~/data/events";
import { clientRepo } from "~/server/apis/client_repo";
import { WebstoreHeader } from "~/components/header/header_webstore";
import { logIntoHubble } from "~/data/classic/logIntoHubble";

type StoreProviderProps = {
  children: JSX.Element;
};

interface StoreState {
  brands: Product[];
  isLoggedIn: boolean;
  v4HomePage: V4HomeResponse | null;
  routeData: WebStoreRouteData | undefined;
  coins: number | undefined;
}

function useWebStoreLayout(
  routeData: Accessor<WebStoreRouteData | undefined>
): WebStoreContextValue {
  const [storeState, setStoreState] = createStore<StoreState>({
    brands: [],
    isLoggedIn: false,
    v4HomePage: null,
    routeData: undefined,
    coins: undefined,
  });

  createEffect(() => {
    setStoreState("brands", routeData()?.brands as Product[]);
    setStoreState("isLoggedIn", routeData()?.isLoggedIn ?? false);
    setStoreState("v4HomePage", routeData()?.v4HomePage ?? null);
    setStoreState("routeData", routeData());
    setStoreState("coins", routeData()?.v4HomePage?.coins.totalAvailable);
  });

  async function refreshV4HomePage() {
    const v4Home = await clientRepo.fetchV4Home();
    setStoreState("v4HomePage", v4Home);
    setStoreState("coins", v4Home.coins.totalAvailable);
  }

  async function loginToWebstore(onAfterLogin: () => void) {
    if (storeState.isLoggedIn) {
      return;
    }
    logIntoHubble(async () => {
      const v4Home$Promise = clientRepo.fetchV4Home();
      const brands$Promise = clientRepo.getProductSearch({
        query: "",
        pagination: { pageNo: 0, limit: 30 },
      });
      const [v4Home, brands] = await Promise.all([
        v4Home$Promise,
        brands$Promise,
      ]);
      const activeBrands = getActiveBrandsFromSearchResponse(brands);
      setStoreState("brands", activeBrands);
      setStoreState("v4HomePage", v4Home);
      setStoreState("coins", v4Home.coins.totalAvailable);
      setStoreState("isLoggedIn", true);
      onAfterLogin();
    }, webEventManager);
  }

  return {
    storeState,
    rnrPortalsJson: () => routeData()?.rnrPortalsJson,
    loginToWebstore,
    refreshV4HomePage,
  };
}

type WebStoreContextValue = {
  storeState: StoreState;
  rnrPortalsJson: () => RnrPortalsJson | undefined;
  loginToWebstore: (onAfterLogin: () => void) => Promise<void>;
  refreshV4HomePage: () => Promise<void>;
};

const WebStoreContext = createContext<WebStoreContextValue>();

const getStoreRouteData$C = cache(getStoreRouteData, "store");

export function WebStoreContextProvider(props: StoreProviderProps) {
  const routeData: Accessor<WebStoreRouteData | undefined> =
    createAsync<WebStoreRouteData>(() => getStoreRouteData$C());

  const contextValue = useWebStoreLayout(routeData);

  return (
    <WebStoreContext.Provider value={contextValue}>
      <Show when={contextValue.storeState.routeData}>
        <div class="h-max w-full ">
          {buildHeader()}
          {props.children}
        </div>
      </Show>
    </WebStoreContext.Provider>
  );

  function buildHeader() {
    const { storeState } = useWebStoreContext();
    const routeData = storeState.routeData!;
    return (
      <WebstoreHeader
        mode={routeData.mode ?? "rnr"}
        coins={storeState.coins ?? 0}
        authMethod={routeData.clientConfig?.authMethod ?? "PHONE"}
        showCoins={() => true}
        clientId={routeData.clientId}
        hideSearchBar={storeState.brands.length == 1}
        headerImageUrl={routeData.clientConfig?.clientLogoUrl}
      />
    );
  }
}

export function useWebStoreContext() {
  const context = useContext(WebStoreContext);
  if (!context) {
    throw new Error(
      "useWebStoreContext must be used within a WebStoreContextProvider"
    );
  }
  return context;
}
